import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_PAD = 12;
const PANEL_GAP = 8;
const MIN_PANEL_HEIGHT = 140;

/**
 * Dropdown
 * ------------------------------------------------------------------
 * Generic trigger + floating panel. Uses a fixed, portaled panel so it
 * is not clipped by overflow:auto ancestors (e.g. session detail player).
 * Height/width clamp to the viewport on small screens.
 */
const Dropdown = ({ trigger, children, align = 'right', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [panelStyle, setPanelStyle] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = useCallback(() => {
    const triggerEl = rootRef.current;
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - PANEL_GAP - VIEWPORT_PAD;
    const spaceAbove = rect.top - PANEL_GAP - VIEWPORT_PAD;
    const openBelow = spaceBelow >= MIN_PANEL_HEIGHT || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(
      MIN_PANEL_HEIGHT,
      Math.min(440, openBelow ? spaceBelow : spaceAbove),
    );
    const maxWidth = Math.min(320, vw - VIEWPORT_PAD * 2);

    const style = {
      position: 'fixed',
      zIndex: 10100,
      maxHeight,
      maxWidth,
      width: 'max-content',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    };

    if (openBelow) {
      style.top = rect.bottom + PANEL_GAP;
      style.bottom = 'auto';
    } else {
      style.bottom = vh - rect.top + PANEL_GAP;
      style.top = 'auto';
    }

    if (align === 'right') {
      const right = Math.max(VIEWPORT_PAD, vw - rect.right);
      style.right = right;
      style.left = 'auto';
      // Keep panel from overflowing left edge on narrow screens.
      style.maxWidth = Math.min(maxWidth, vw - right - VIEWPORT_PAD);
    } else {
      const left = Math.min(
        Math.max(VIEWPORT_PAD, rect.left),
        vw - VIEWPORT_PAD - 160,
      );
      style.left = left;
      style.right = 'auto';
      style.maxWidth = Math.min(maxWidth, vw - left - VIEWPORT_PAD);
    }

    setPanelStyle(style);
  }, [align]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle(null);
      return undefined;
    }
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    // Capture scroll from nested overflow containers (player detail).
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      const inTrigger = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inTrigger && !inPanel) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const toggle = (event) => {
    event?.stopPropagation?.();
    setIsOpen((prev) => !prev);
  };

  const panel =
    isOpen && panelStyle
      ? createPortal(
          <div
            ref={panelRef}
            className={`dropdown__panel dropdown__panel--${align} dropdown__panel--portaled`}
            style={panelStyle}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            {typeof children === 'function'
              ? children({ close: () => setIsOpen(false) })
              : children}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`dropdown ${className}`} ref={rootRef}>
      {React.isValidElement(trigger) ? (
        React.cloneElement(trigger, {
          onClick: toggle,
          'aria-expanded': isOpen,
        })
      ) : (
        <button
          type="button"
          className="dropdown__trigger"
          aria-expanded={isOpen}
          onClick={toggle}
        >
          {trigger}
        </button>
      )}
      {panel}
    </div>
  );
};

export default Dropdown;
